import type { Community } from "@packages/sdk";
import { CommunityContractPackageTier, CommunityType } from "@packages/sdk";

export type HadResourcesVersion = "parish" | "school";

export type HadResourceLink = {
  /** snake_case id sent as the `link` property of `Tapped Resource Link` */
  analyticsId: string;
  descriptionMessage: string;
  /** `mailto:` links open the mail client in place; others open a new tab */
  href: string;
  message: string;
};

export type HadResourceEssential = {
  descriptionMessage: string;
  href: string;
  /** Square thumbnail under `public/` */
  image: string;
  message: string;
};

/**
 * Which version of the Resources tab a community gets, or `null` when the tab
 * is not available (Faith Formation, networks, small groups, non-admins, etc.).
 */
export const getHadResourcesVersion = (
  community: Pick<Community, "is_admin" | "type">,
): HadResourcesVersion | null => {
  if (!community.is_admin) return null;
  if ([CommunityType.parish, CommunityType.basic].includes(community.type))
    return "parish";
  if (community.type === CommunityType.school) return "school";
  return null;
};

export const HAD_RESOURCES_ESSENTIALS: Record<
  HadResourcesVersion,
  HadResourceEssential
> = {
  parish: {
    descriptionMessage: "had_resources_partner_resource_center_description",
    href: "https://drive.google.com/drive/folders/1kKhf3UTBqZIDcZVfaa4Pmm9rPGUXLPPh",
    image: "/assets/had-resources-parish.webp",
    message: "had_resources_partner_resource_center",
  },
  school: {
    descriptionMessage: "had_resources_teachers_resource_center_description",
    href: "https://teachers.hallow.com",
    image: "/assets/had-resources-school.webp",
    message: "had_resources_teachers_resource_center",
  },
};

const HAD_RESOURCES_HELP_CENTER_LINK: HadResourceLink = {
  analyticsId: "help_center",
  descriptionMessage: "had_resources_link_help_center_description",
  href: "https://help.hallow.com/",
  message: "had_resources_link_help_center",
};

const HAD_RESOURCES_PARISH_HELP_CENTER_LINK: HadResourceLink = {
  ...HAD_RESOURCES_HELP_CENTER_LINK,
  href: "https://help.hallow.com/en/collections/10447689-pray-with-your-parish-on-hallow",
};

/**
 * Quicklinks render in a two-column grid that fills column by column: the first
 * half of each list is the left column, the second half the right column.
 */
export const HAD_RESOURCES_PARISH_QUICKLINKS: HadResourceLink[] = [
  {
    analyticsId: "start_here",
    descriptionMessage: "had_resources_link_start_here_description",
    href: "https://drive.google.com/drive/folders/1IKjqdxX6mNR8LqTxvvgQyFLoL-22S1UI",
    message: "had_resources_link_new_partners_start_here",
  },
  {
    analyticsId: "partner_onboarding_instructions",
    descriptionMessage:
      "had_resources_link_partner_onboarding_instructions_description",
    href: "https://docs.google.com/document/d/1y9MjdvLQt5byEbmLMzGAr6vydF2nn-e6Q-uNPX3WGxY/edit",
    message: "had_resources_link_parishioner_onboarding_instructions",
  },
  {
    analyticsId: "evergreen_parish_kit",
    descriptionMessage: "had_resources_link_evergreen_parish_kit_description",
    href: "https://drive.google.com/drive/folders/1_Ymvnp1lRKxlI8ikkXfsW-2QDP3GMCSS",
    message: "had_resources_link_evergreen_parish_kit",
  },
  {
    analyticsId: "hallow_partner_manual",
    descriptionMessage: "had_resources_link_hallow_partner_manual_description",
    href: "https://docs.google.com/document/d/1aHg2B6Pw-VwMcrAFquEc2haVTZrr_WKRO-aF-RdmRl8/edit",
    message: "had_resources_link_hallow_partner_manual",
  },
  {
    analyticsId: "hallow_story_video",
    descriptionMessage: "had_resources_link_hallow_story_video_description",
    href: "https://drive.google.com/file/d/1443yzSvxHPvZhBonb8oH0UPzI48QqZy3/view",
    message: "had_resources_link_hallow_story_video",
  },
];

export const HAD_RESOURCES_SCHOOL_QUICKLINKS: HadResourceLink[] = [
  {
    analyticsId: "how_to_roster",
    descriptionMessage: "had_resources_link_how_to_roster_description",
    href: "https://teachers.hallow.com/content-page/how-to-admin-dashboard-rostering/",
    message: "had_resources_link_how_to_roster",
  },
  {
    analyticsId: "creating_classes",
    descriptionMessage: "had_resources_link_creating_classes_description",
    href: "https://teachers.hallow.com/content-page/how-to-creating-classes-in-hallow/",
    message: "had_resources_link_creating_classes",
  },
  {
    analyticsId: "parent_permission_slip",
    descriptionMessage: "had_resources_link_parent_permission_slip_description",
    href: "https://teachers.hallow.com/content-page/parent-permission-slip/",
    message: "had_resources_link_parent_permission_slip",
  },
  {
    analyticsId: "introducing_hallow",
    descriptionMessage: "had_resources_link_introducing_hallow_description",
    href: "https://teachers.hallow.com/content-page/introducing-hallow-to-students/",
    message: "had_resources_link_introducing_hallow",
  },
  {
    analyticsId: "student_login_cards",
    descriptionMessage: "had_resources_link_student_login_cards_description",
    href: "https://teachers.hallow.com/content-page/student-login-cards-2/",
    message: "had_resources_link_student_login_cards",
  },
  HAD_RESOURCES_HELP_CENTER_LINK,
];

export const HAD_RESOURCES_QUICKLINKS: Record<
  HadResourcesVersion,
  HadResourceLink[]
> = {
  parish: HAD_RESOURCES_PARISH_QUICKLINKS,
  school: HAD_RESOURCES_SCHOOL_QUICKLINKS,
};

/** "Have a question?" section, shown after Quick Links (Parish version only) */
export const HAD_RESOURCES_HAVE_A_QUESTION_LINKS: Partial<
  Record<HadResourcesVersion, HadResourceLink[]>
> = {
  parish: [
    {
      analyticsId: "parishioner_help",
      descriptionMessage: "had_resources_link_parishioner_help_description",
      href: "mailto:parishsupport@hallow.app",
      message: "had_resources_link_parishioner_help",
    },
    HAD_RESOURCES_PARISH_HELP_CENTER_LINK,
  ],
};

const HAD_RESOURCES_GIVING_WEBINAR_LINK: HadResourceLink = {
  analyticsId: "giving_webinar",
  descriptionMessage: "had_resources_link_giving_webinar_description",
  href: "https://hallow.zoom.us/meeting/register/osqhdeOIRey6Qz-WGLRaCA#/registration",
  message: "had_resources_link_giving_webinar",
};

const HAD_RESOURCES_GIVING_WEBINAR_TIERS: CommunityContractPackageTier[] = [
  CommunityContractPackageTier.growth,
  CommunityContractPackageTier.expansion,
  CommunityContractPackageTier.mobilization,
];

/**
 * Quick Links for a version, plus the Giving Webinar sign-up as the last card
 * when the community's partnership contract tier includes it. Pass `undefined`
 * while the contract is loading or unavailable so the card stays hidden.
 */
export const getHadResourcesQuicklinks = (
  version: HadResourcesVersion,
  packageTier: CommunityContractPackageTier | null | undefined,
): HadResourceLink[] =>
  packageTier && HAD_RESOURCES_GIVING_WEBINAR_TIERS.includes(packageTier)
    ? [...HAD_RESOURCES_QUICKLINKS[version], HAD_RESOURCES_GIVING_WEBINAR_LINK]
    : HAD_RESOURCES_QUICKLINKS[version];
